import { MessageSquare } from 'lucide-react';
import type { ShareState } from '#shared/types';
import { Button } from '#client/components/ui/Button';

/**
 * The reason a report cannot be sent, in the words of whoever has to fix it.
 *
 * A disabled control with no explanation is the thing people file bugs about,
 * so every blocked state says what is in the way — and each one names an action
 * rather than a condition. The server computes which of these applies; the UI
 * never re-derives it.
 */
const BLOCKED: Record<Exclude<ShareState['readiness'], 'ready'>, string> = {
  not_configured: 'WhatsApp is not set up on this server. An admin can configure it.',
  no_number: 'This patient has no phone number on file. Edit the patient to add one.',
  invalid_number:
    'That number cannot receive WhatsApp — it may be a landline. Edit the patient to correct it.',
  no_consent:
    'Consent has not been recorded. Edit the patient and tick “agrees to receive reports on WhatsApp”.',
  not_finalized: 'Nothing on this visit is finalized yet, so there is nothing to send.',
  in_flight: 'A send is already in progress for this patient.',
};

interface ShareWhatsAppButtonProps {
  state: ShareState | undefined;
  loading: boolean;
  onOpen: () => void;
}

export function ShareWhatsAppButton({ state, loading, onOpen }: ShareWhatsAppButtonProps) {
  const readiness = state?.readiness;
  const blocked = readiness && readiness !== 'ready' ? BLOCKED[readiness] : undefined;

  return (
    <Button
      variant="secondary"
      icon={<MessageSquare />}
      // Disabled while the verdict is still loading, so the button cannot be
      // pressed before the server has said whether it is allowed.
      disabled={loading || state === undefined || blocked !== undefined}
      title={blocked ?? 'Send the finalized report to this patient on WhatsApp'}
      onClick={onOpen}
    >
      Send on WhatsApp
    </Button>
  );
}
